import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Image, X, Upload, Info, Copy, Calendar, Tag, TrendingUp } from 'lucide-react';
import { BeforeAfterCase } from '@/types/gallery';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

interface ClinicOption {
  id: string;
  name: string;
}

interface AddCaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (caseData: Omit<BeforeAfterCase, 'id' | 'doctorId' | 'createdAt' | 'updatedAt'>) => void;
  clinics: ClinicOption[];
  editCase?: BeforeAfterCase;
}

// Sample treatment types for multi-select
const treatmentTypes = [
  "Cosmetic Dentistry",
  "Implants",
  "Orthodontics",
  "Veneers",
  "Whitening",
  "Smile Design",
  "Crowns",
  "Bridges",
  "Root Canal",
  "Extraction"
];

// Boost duration options with pricing
const boostOptions = [
  { id: '7days', label: '7 Days', price: 29.99 },
  { id: '14days', label: '14 Days', price: 49.99 },
  { id: '30days', label: '30 Days', price: 89.99 }
];

const AddCaseModal: React.FC<AddCaseModalProps> = ({ 
  open, 
  onOpenChange, 
  onSubmit, 
  clinics,
  editCase 
}) => {
  const { toast } = useToast();
  const [title, setTitle] = useState(editCase?.title || '');
  const [clinicId, setClinicId] = useState(editCase?.clinicId || '');
  const [beforeImage, setBeforeImage] = useState(editCase?.beforeImage || '');
  const [afterImage, setAfterImage] = useState(editCase?.afterImage || '');
  const [isPublic, setIsPublic] = useState(editCase?.isPublic ?? true);
  const [description, setDescription] = useState(editCase?.description || '');
  const [beforePreview, setBeforePreview] = useState<string | null>(editCase?.beforeImage || null);
  const [afterPreview, setAfterPreview] = useState<string | null>(editCase?.afterImage || null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  const [beforeDate, setBeforeDate] = useState(editCase?.beforeDate ? new Date(editCase.beforeDate) : new Date());
  const [afterDate, setAfterDate] = useState(editCase?.afterDate ? new Date(editCase.afterDate) : new Date());
  const [selectedTreatmentTypes, setSelectedTreatmentTypes] = useState<string[]>(
    editCase?.treatmentTypes ? editCase.treatmentTypes : 
    editCase?.treatmentType ? [editCase.treatmentType] : []
  );
  const [publicLabel, setPublicLabel] = useState(editCase?.publicLabel || '');
  const [boostEnabled, setBoostEnabled] = useState(false);
  const [selectedBoostOption, setSelectedBoostOption] = useState(boostOptions[0].id);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  const deepLinkPreview = `${window.location.origin}/doctor-profile/1?case=${editCase?.id || 'new'}`;

  useEffect(() => {
    if (open) {
      setTitle(editCase?.title || '');
      setClinicId(editCase?.clinicId || '');
      setBeforeImage(editCase?.beforeImage || '');
      setAfterImage(editCase?.afterImage || '');
      setIsPublic(editCase?.isPublic ?? true);
      setDescription(editCase?.description || '');
      setBeforePreview(editCase?.beforeImage || null);
      setAfterPreview(editCase?.afterImage || null);
      setFormErrors({});

      setBeforeDate(editCase?.beforeDate ? new Date(editCase.beforeDate) : new Date());
      setAfterDate(editCase?.afterDate ? new Date(editCase.afterDate) : new Date());
      setSelectedTreatmentTypes(
        editCase?.treatmentTypes ? editCase.treatmentTypes : 
        editCase?.treatmentType ? [editCase.treatmentType] : []
      );
      setPublicLabel(editCase?.publicLabel || '');
      setBoostEnabled(editCase?.boosted || false);
      setSelectedBoostOption(boostOptions[0].id);
      setIsProcessingPayment(false);
    }
  }, [open, editCase]);

  const handleBeforeImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.match('image/jpeg|image/png')) {
        setFormErrors(prev => ({ ...prev, beforeImage: 'Only JPG and PNG files are allowed' }));
        return;
      }
      
      setFormErrors(prev => {
        const newErrors = {...prev};
        delete newErrors.beforeImage;
        return newErrors;
      });
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setBeforePreview(result);
        setBeforeImage(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAfterImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.match('image/jpeg|image/png')) {
        setFormErrors(prev => ({ ...prev, afterImage: 'Only JPG and PNG files are allowed' }));
        return;
      }
      
      setFormErrors(prev => {
        const newErrors = {...prev};
        delete newErrors.afterImage;
        return newErrors;
      });
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setAfterPreview(result);
        setAfterImage(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearBeforeImage = () => {
    setBeforePreview(null);
    setBeforeImage('');
  };

  const clearAfterImage = () => {
    setAfterPreview(null);
    setAfterImage('');
  };
  
  const toggleTreatmentType = (type: string) => {
    setSelectedTreatmentTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const copyDeepLink = () => {
    navigator.clipboard.writeText(deepLinkPreview)
      .then(() => {
        toast({
          title: "Link copied!",
          description: "Case link has been copied to clipboard",
        });
      })
      .catch(() => {
        toast({
          title: "Failed to copy",
          description: "Could not copy the link to clipboard",
          variant: "destructive"
        });
      });
  };

  const handleProcessPayment = () => {
    setIsProcessingPayment(true);
    setTimeout(() => {
      toast({
        title: "Payment successful!",
        description: `Your case has been boosted for ${
          boostOptions.find(opt => opt.id === selectedBoostOption)?.label
        }.`,
      });
      setIsProcessingPayment(false);
      handleSubmit(true);
    }, 2000);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!title.trim()) errors.title = 'Title is required';
    if (!clinicId) errors.clinicId = 'Please select a clinic';
    if (!beforeImage) errors.beforeImage = 'Before image is required';
    if (!afterImage) errors.afterImage = 'After image is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (withBoost = false) => {
    if (!validateForm()) return;
    
    const clinicName = clinics.find(clinic => clinic.id === clinicId)?.name || '';
    
    onSubmit({
      title,
      clinicId,
      clinicName,
      beforeImage,
      afterImage,
      isPublic,
      treatmentType: selectedTreatmentTypes[0] || '',
      treatmentTypes: selectedTreatmentTypes,
      description,
      beforeDate: beforeDate.toISOString(),
      afterDate: afterDate.toISOString(),
      publicLabel: publicLabel || title,
      boosted: withBoost ? true : false,
      boostDuration: withBoost ? selectedBoostOption : undefined,
      boostExpiry: withBoost ? 
        new Date(Date.now() + parseInt(selectedBoostOption.replace('days', '')) * 24 * 60 * 60 * 1000).toISOString() : 
        undefined
    });
    
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(state) => {
      if (isProcessingPayment) return;
      onOpenChange(state);
    }}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editCase ? 'Edit Case' : 'Add New Case'}</DialogTitle>
          <DialogDescription>
            Add before and after images to showcase your work.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="beforeImage">Before Image</Label>
              {beforePreview ? (
                <div className="relative h-40 w-full rounded-md overflow-hidden border">
                  <img 
                    src={beforePreview} 
                    alt="Before" 
                    className="h-full w-full object-cover"
                  />
                  <Button 
                    variant="destructive" 
                    size="icon" 
                    className="absolute top-2 right-2 h-6 w-6 rounded-full"
                    onClick={clearBeforeImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="flex flex-col items-center justify-center h-40 border-2 border-dashed rounded-md p-4 hover:bg-muted/50 cursor-pointer"
                  onClick={() => document.getElementById('beforeImage')?.click()}
                >
                  <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Click to upload before image</p>
                  <p className="text-xs text-muted-foreground mt-1">JPG, PNG only</p>
                </div>
              )}
              <input 
                id="beforeImage" 
                type="file" 
                className="hidden" 
                accept="image/jpeg,image/png" 
                onChange={handleBeforeImageChange}
              />
              {formErrors.beforeImage && (
                <p className="text-sm text-destructive">{formErrors.beforeImage}</p>
              )}
              
              <div className="flex items-center space-x-2 mt-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="beforeDate" className="text-sm">Date Taken:</Label>
                <Input 
                  id="beforeDate" 
                  type="date" 
                  value={format(beforeDate, 'yyyy-MM-dd')}
                  onChange={(e) => setBeforeDate(new Date(e.target.value))}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="afterImage">After Image</Label>
              {afterPreview ? (
                <div className="relative h-40 w-full rounded-md overflow-hidden border">
                  <img 
                    src={afterPreview} 
                    alt="After" 
                    className="h-full w-full object-cover"
                  />
                  <Button 
                    variant="destructive" 
                    size="icon" 
                    className="absolute top-2 right-2 h-6 w-6 rounded-full"
                    onClick={clearAfterImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="flex flex-col items-center justify-center h-40 border-2 border-dashed rounded-md p-4 hover:bg-muted/50 cursor-pointer"
                  onClick={() => document.getElementById('afterImage')?.click()}
                >
                  <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Click to upload after image</p>
                  <p className="text-xs text-muted-foreground mt-1">JPG, PNG only</p>
                </div>
              )}
              <input 
                id="afterImage" 
                type="file" 
                className="hidden" 
                accept="image/jpeg,image/png" 
                onChange={handleAfterImageChange}
              />
              {formErrors.afterImage && (
                <p className="text-sm text-destructive">{formErrors.afterImage}</p>
              )}
              
              <div className="flex items-center space-x-2 mt-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="afterDate" className="text-sm">Date Taken:</Label>
                <Input 
                  id="afterDate" 
                  type="date" 
                  value={format(afterDate, 'yyyy-MM-dd')}
                  onChange={(e) => setAfterDate(new Date(e.target.value))}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="title">Case Title / Treatment Name (Internal)</Label>
            <Input 
              id="title" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              placeholder="e.g., Dental Implant Case"
            />
            {formErrors.title && (
              <p className="text-sm text-destructive">{formErrors.title}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="publicLabel">Public Case Label (Optional)</Label>
            <Input 
              id="publicLabel" 
              value={publicLabel} 
              onChange={(e) => setPublicLabel(e.target.value)} 
              placeholder="Short public-facing title"
            />
            <p className="text-xs text-muted-foreground">
              This label will be displayed on the public gallery. If left empty, the case title will be used.
            </p>
          </div>
          
          <div className="space-y-2">
            <Label className="block mb-2">Treatment Types</Label>
            <div className="flex flex-wrap gap-2">
              {treatmentTypes.map((type) => (
                <Badge 
                  key={type}
                  variant={selectedTreatmentTypes.includes(type) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleTreatmentType(type)}
                >
                  {selectedTreatmentTypes.includes(type) && '✓ '}
                  {type}
                </Badge>
              ))}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="clinic">Select Clinic</Label>
            <Select value={clinicId} onValueChange={setClinicId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a clinic" />
              </SelectTrigger>
              <SelectContent>
                {clinics.map((clinic) => (
                  <SelectItem key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formErrors.clinicId && (
              <p className="text-sm text-destructive">{formErrors.clinicId}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea 
              id="description" 
              value={description || ''} 
              onChange={(e) => setDescription(e.target.value)} 
              placeholder="Briefly describe the treatment"
              className="resize-none h-20"
            />
          </div>

          {editCase && (
            <div className="space-y-2 bg-muted/30 p-3 rounded-md">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Case Direct Link</Label>
                <Button variant="ghost" size="sm" onClick={copyDeepLink}>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </Button>
              </div>
              <div className="text-xs bg-background rounded p-2 overflow-x-auto">
                {deepLinkPreview}
              </div>
              <p className="text-xs text-muted-foreground">
                Share this link to let others view this specific case on your profile.
              </p>
            </div>
          )}
          
          <div className="flex items-center space-x-2 pt-2">
            <Checkbox 
              id="visibility" 
              checked={isPublic} 
              onCheckedChange={(checked) => setIsPublic(checked as boolean)} 
            />
            <Label htmlFor="visibility" className="cursor-pointer">
              Show this case publicly on my profile
            </Label>
          </div>
          
          <div className="flex items-start mt-2 text-sm text-muted-foreground">
            <Info className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
            <p>Public cases will be visible on your doctor profile and the global before & after gallery.</p>
          </div>
          
          <div className="mt-6 border-t pt-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <h3 className="font-medium text-lg">Promote This Case</h3>
            </div>
            
            <div className="flex items-center space-x-2 mt-3">
              <Checkbox 
                id="boost" 
                checked={boostEnabled}
                disabled={isProcessingPayment}
                onCheckedChange={(checked) => setBoostEnabled(checked as boolean)} 
              />
              <Label htmlFor="boost" className="cursor-pointer">
                Boost this case for better visibility on homepage and discovery
              </Label>
            </div>
            
            {boostEnabled && (
              <div className="mt-4 pl-6">
                <div className="space-y-3">
                  <Label className="text-sm">Boost Duration</Label>
                  
                  <div className="grid grid-cols-3 gap-3">
                    {boostOptions.map((option) => (
                      <div 
                        key={option.id}
                        className={`border rounded-md p-3 text-center cursor-pointer transition-colors ${
                          selectedBoostOption === option.id ? 'border-primary bg-primary/10' : 'hover:bg-muted/50'
                        }`}
                        onClick={() => !isProcessingPayment && setSelectedBoostOption(option.id)}
                      >
                        <div className="font-medium">{option.label}</div>
                        <div className="text-primary text-lg font-bold">${option.price}</div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="text-xs text-muted-foreground mt-2">
                    Boosted cases appear in the highlighted section of the homepage and discovery pages.
                  </div>
                  
                  <Button 
                    className="mt-3 w-full"
                    onClick={handleProcessPayment}
                    disabled={isProcessingPayment}
                  >
                    {isProcessingPayment ? 'Processing...' : 'Proceed to Payment'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isProcessingPayment}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleSubmit(false)}
            disabled={isProcessingPayment}
          >
            {editCase ? 'Update' : 'Add'} Case
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddCaseModal;
